import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MineAvatarDetails,
  MineAvatarGQL,
  UpdateMineAvatarGQL,
  UploadContext,
} from '../../../../../../graphql';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  filter,
  first,
  pluck,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { ZalamoDialogService } from 'ZalamoLibrary/src/lib/ui/zalamo-dialog/zalamo-dialog.service';
import { AvatarCropperComponent } from './avatar-cropper/avatar-cropper.component';
import { UploadService } from 'ZalamoLibrary/src/lib/upload/upload.service';
import { TypedFormBuilder } from 'ZalamoLibrary/src/lib/ui/form/TypedFormBuilder';

@Component({
  selector: 'app-avatar-setter',
  templateUrl: './avatar-setter.component.html',
  styleUrls: ['./avatar-setter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AvatarSetterComponent extends AliveState implements OnInit {
  avatar = new UntypedFormControl();
  croppedAvatar = new UntypedFormControl();
  avatarUploadContext: UploadContext = UploadContext.UserAvatar;
  lastCrop = null;
  model = this.form.typedGroup<Omit<MineAvatarDetails, 'userId'>>({
    file: [],
    cropped: [],
  });

  constructor(
    private store: Store,
    private updateMineAvatarGQL: UpdateMineAvatarGQL,
    private mineAvatarGQL: MineAvatarGQL,
    private zalamoDialogService: ZalamoDialogService,
    private uploadService: UploadService,
    private form: TypedFormBuilder,
  ) {
    super();
  }

  crop() {
    const ref = this.zalamoDialogService.openFull(AvatarCropperComponent);
    ref.componentInstance.avatar = this.model.value.file;
    ref.componentInstance.lastCrop = this.lastCrop;
    ref.componentInstance.saved
      .pipe(
        tap(async (croppedAvatar) => {
          this.uploadService
            .uploadSingleFile(
              (await (await fetch(croppedAvatar.base64)).blob()) as File,
              UploadContext.UserAvatar,
              'me',
            )
            .pipe(
              tap((cropped) => this.model.patchValue({ cropped })),
              first(),
            )
            .subscribe();
          ref.close();
        }),
        takeUntil(ref.afterClosed()),
      )
      .subscribe();
  }

  ngOnInit(): void {
    let updateInProgress = false;
    this.subscribeWhileAlive(
      this.mineAvatarGQL.fetch({}).pipe(
        pluck('data', 'getMineAvatar'),
        tap((avatar) => {
          updateInProgress = true;
          this.model.patchValue(avatar);
          updateInProgress = false;
        }),
      ),
      this.model.controls.file.valueChanges.pipe(
        filter(() => !updateInProgress),
        tap(() => this.model.patchValue({ cropped: null })),
      ),
      this.model.valueChanges.pipe(
        filter(() => !updateInProgress),
        debounceTime(100),
        switchMap(({ file, cropped }) => {
          return this.updateMineAvatarGQL.mutate({
            variables: {
              file,
              cropped,
            },
          });
        }),
      ),
    );
  }
}
